import { ref } from '@vue/composition-api';
import { useReactiveMap } from '@/hooks/useReactiveMap';
import dependencyLoader from '@utils/dependencyLoader';
import notifier from '@utils/dependencyLoader/notifier';
import { arrayify } from '@tencent/ui-core/lib/utils';
import { debounce } from 'lodash';
import openProviderModal from './provider-modal';
import { ConsumerMessageType, ProviderMessageType, PROVIDER_SOURCE, CONSUMER_SOURCE, BROADCAST_CHANNEL_ID, } from './consts';
import { previewDebugger, logProviderSend, logProviderReceive, logError, OperationType } from './debugger';
/**
 * 编辑器使用的 previewProvider
 *
 * provider的意思是提供页面信息的一方
 */
export const usePreviewProvider = (previewSessionKey) => {
    // 我们只能在createdHook中获取到editor实例，所以这里需要一个全局变量来存储editor实例
    let providerEditor = null;
    const getPayload = () => ({
        baseData: providerEditor?.baseData,
        projectInfo: providerEditor?.projectInfo,
    });
    const basePreviewer = usePreviewBaseProvider(previewSessionKey, getPayload);
    const editorCreatedHook = (editor) => {
        providerEditor = editor;
        basePreviewer.createHook();
    };
    const editorDestroyedHook = () => {
        basePreviewer.destroyHook();
    };
    const updatePageConfig = () => {
        basePreviewer.postMessage(getPayload(), ProviderMessageType.updatePageConfig);
    };
    const switchActiveKeyHook = (_editor, key) => {
        if (key === 'renderer') {
            updatePageConfig();
        }
    };
    return {
        ...basePreviewer,
        editorCreatedHook,
        editorDestroyedHook,
        switchActiveKeyHook,
        updatePageConfig,
    };
};
/**
 * 用于预览模式的基础数据提供者
 *
 * 用于拓展不同场景下需要使用预览模式的组件
 *
 * @param previewSessionKey 用于区别不同的预览会话
 * @returns
 */
export const usePreviewBaseProvider = (previewSessionKey, getPayload) => {
    const broadcastChannel = ref(null);
    const connectedPreviewers = useReactiveMap();
    const providerEnabled = ref(true);
    const duplicateModalOpen = ref(false);
    let pingCheckKillList = new Set();
    const pingCheckTimer = setInterval(() => {
        if (document.visibilityState !== 'visible')
            return;
        pingCheckKillList.forEach((previewerId) => {
            connectedPreviewers.delete(previewerId);
        });
        pingCheckKillList = new Set(connectedPreviewers.data.keys());
        postMessage({}, ProviderMessageType.ping);
    }, 30e3);
    const sendDataSourceUpdate = () => {
        postMessage({}, ProviderMessageType.updateDataSource);
    };
    // 由于打印日志前，需要先发消息给consumer，等到consumer将它的日志发过来之后，再打印
    // 所以增加了一个回调机制，目前只有日志打印的场景用到
    const messageCallback = {};
    const registerMessageCallback = (messageType, callback) => {
        if (!messageCallback[messageType]) {
            messageCallback[messageType] = [];
        }
        messageCallback[messageType].push(callback);
    };
    const unregisterMessageCallback = (messageType, callback) => {
        if (messageCallback[messageType]) {
            messageCallback[messageType] = messageCallback[messageType].filter(cb => cb !== callback);
        }
    };
    const updateDeps = debounce((module) => postMessage(module, ProviderMessageType.updateDependency), 500);
    const handleDepsChanged = (info) => {
        updateDeps({
            dependencies: info.allDependencies,
            bundle: info.bundle,
            packageJson: info.packageJson,
        });
    };
    const createHook = () => {
        broadcastChannel.value = new BroadcastChannel(`${BROADCAST_CHANNEL_ID}_${previewSessionKey}`);
        broadcastChannel.value.onmessage = (event) => {
            const { source, messageType, previewerId } = event.data;
            // 记录接收日志
            if (source === CONSUMER_SOURCE) {
                logProviderReceive(messageType, event.data.payload, previewerId);
            }
            if (source === CONSUMER_SOURCE && previewerId) {
                let previewer = connectedPreviewers.get(previewerId);
                if (!previewer) {
                    previewer = {
                        id: previewerId,
                        postMessage: (payload, messageType) => postMessage(payload, messageType, [previewerId]),
                    };
                    connectedPreviewers.set(previewerId, previewer);
                }
                // handle messages from a previewer
                switch (messageType) {
                    case ConsumerMessageType.pong:
                        pingCheckKillList.delete(previewerId);
                        break;
                    case ConsumerMessageType.fetchConfig:
                        if (!providerEnabled.value) {
                            return;
                        }
                        previewer.postMessage(getPayload(), ProviderMessageType.updatePageConfig);
                        const info = dependencyLoader.getInfo();
                        updateDeps({
                            dependencies: info.allDependencies,
                            bundle: info.bundle,
                            packageJson: info.packageJson,
                        });
                        break;
                    case ConsumerMessageType.closed:
                        connectedPreviewers.delete(previewerId);
                        break;
                    case ConsumerMessageType.editorCreatedAck:
                        // do nothing - 上面添加previewer的逻辑已经会记录这个previewer了，目前不需要做其他事情
                        break;
                }
            }
            if (messageType === ConsumerMessageType.debuggerLogs) {
                previewDebugger.addConsumerLogs(event.data.payload.logs || []);
            }
            if (messageCallback[messageType]) {
                messageCallback[messageType].forEach((callback) => {
                    callback(event);
                });
            }
            // 同一个channel id下只允许有一个provider，如果有新的provider创建，将之前的provider关闭
            if (source === PROVIDER_SOURCE
                && messageType === ProviderMessageType.providerCreated) {
                providerEnabled.value = false;
                if (duplicateModalOpen.value || connectedPreviewers.size === 0)
                    return;
                openProviderModal(null).then((res) => {
                    if (!res.success) {
                        return;
                    }
                    activeProvider();
                });
                duplicateModalOpen.value = true;
            }
        };
        postMessage({}, ProviderMessageType.providerCreated);
        notifier.onDepsChanged(handleDepsChanged);
        window.addEventListener('beforeunload', destroyHook);
        window?.xy_event_bus?.$on('collection-update', sendDataSourceUpdate);
    };
    const destroyHook = () => {
        window.removeEventListener('beforeunload', destroyHook);
        clearInterval(pingCheckTimer);
        if (broadcastChannel.value) {
            postMessage({}, ProviderMessageType.providerDestroyed);
            broadcastChannel.value.close();
            broadcastChannel.value = null;
        }
        notifier.offDepsChanged(handleDepsChanged);
        window?.xy_event_bus?.$off('collection-update', sendDataSourceUpdate);
    };
    const postMessage = (payload, messageType, targetPreviewerIds) => {
        try {
            broadcastChannel.value?.postMessage({
                messageType,
                source: PROVIDER_SOURCE,
                payload,
                targetPreviewerIds: targetPreviewerIds?.length
                    ? arrayify(targetPreviewerIds)
                    : null,
            });
            // 记录发送日志
            logProviderSend(messageType, payload, targetPreviewerIds ? arrayify(targetPreviewerIds) : undefined);
        }
        catch (error) {
            logError(OperationType.PROVIDER_SEND, messageType, `Failed to send message: ${error}`, payload);
        }
    };
    // 激活当前provider
    const activeProvider = () => {
        providerEnabled.value = true;
        postMessage({}, ProviderMessageType.providerCreated);
        setTimeout(() => {
            postMessage(getPayload(), ProviderMessageType.updatePageConfig);
        }, 500);
        duplicateModalOpen.value = false;
    };
    const printAllLogs = () => {
        const callback = () => {
            clearTimeout(timer);
            previewDebugger.print();
            unregisterMessageCallback(ConsumerMessageType.debuggerLogs, callback);
        };
        registerMessageCallback(ConsumerMessageType.debuggerLogs, callback);
        postMessage({}, ProviderMessageType.requestDebuggerLogs);
        const timer = setTimeout(() => {
            console.log('获取consumer日志超时');
            previewDebugger.print();
        }, 5000);
    };
    // debug的时候，通过window.printPreviewLog[x]来打印日志
    // 注意这个是个数组，因为可能会创建多个provider，默认的预览模式是第一个
    if (!window.printPreviewLog) {
        window.printPreviewLog = [];
    }
    window.printPreviewLog.push(printAllLogs);
    return {
        createHook,
        destroyHook,
        postMessage,
        connectedPreviewers,
        providerEnabled,
        activeProvider,
    };
};
